import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AssessmentSessionContext from '../../context/AssessmentSessionContext';
import { validatePublished, getAssessmentQuestions, getDriveInfo } from '../../api/publicAssessApi';
import { FaLaptop, FaChevronRight, FaChevronLeft, FaTimesCircle, FaCloudUploadAlt, FaTh, FaTimes } from 'react-icons/fa';
import CustomAlertModal from '../../components/common/CustomAlertModal';
import CodingPanel from '../../components/student/CodingPanel';

const AssessmentPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { 
    status, 
    questions, 
    answers, 
    currentQuestionIndex, 
    timeRemaining, 
    violations, 
    isSubmitted,
    driveInfo,
    initSession,
    setAnswer, 
    setCurrentQuestion, 
    tickTimer, 
    addViolation, 
    submitAssessment 
  } = useContext(AssessmentSessionContext);

  const lastViolationAtRef = useRef(0);

  const [publishedError, setPublishedError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fetchingQs, setFetchingQs] = useState(false);
  const [showMobilePalette, setShowMobilePalette] = useState(false);
  // Coding questions: saved editor content + best submission per question (restored on resume)
  const [codeDrafts, setCodeDrafts] = useState({});
  const [codeSubmissions, setCodeSubmissions] = useState({});
  const [codeMaxSubmissions, setCodeMaxSubmissions] = useState(5);
  
  const [alertConfig, setAlertConfig] = useState({ show: false, message: '', onConfirm: null });

  const showCustomAlert = useCallback((message, onConfirm = null) => {
    setAlertConfig({ show: true, message, onConfirm });
  }, []);

  const hideCustomAlert = useCallback(() => {
    const { onConfirm } = alertConfig;
    setAlertConfig({ show: false, message: '', onConfirm: null });
    if (onConfirm) onConfirm();
  }, [alertConfig]);

  console.log('ASSESSMENT PAGE STATE', {
    status,
    fetchingQs,
    publishedError,
    questions,
    driveInfo,
    timeRemaining,
  });

  useEffect(() => {
    if (!token) return;

    let isMounted = true;

    const prepare = async () => {
      setFetchingQs(true);
      setPublishedError('');

      try {
        await validatePublished(token);

        const studentToken = localStorage.getItem(`studentToken:${token}`) || localStorage.getItem('studentToken');
        if (!studentToken) {
          if (isMounted) {
            navigate(`/register/${token}`, { replace: true });
          }
          return;
        }

        const [driveRes, qRes] = await Promise.all([getDriveInfo(token), getAssessmentQuestions(token)]);

        if (!isMounted) return;

        console.log('QUESTION RESPONSE', qRes?.data);

        const unwrapApiPayload = (payload) => {
          if (!payload || typeof payload !== 'object') return payload;

          if (payload && typeof payload.success === 'boolean' && 'data' in payload) {
            return unwrapApiPayload(payload.data);
          }

          if (Array.isArray(payload)) return payload;

          return payload;
        };

        const driveInfoData = unwrapApiPayload(driveRes?.data);
        const questionsPayload = unwrapApiPayload(qRes?.data) || {};
        const questionsData = Array.isArray(questionsPayload?.questions) ? questionsPayload.questions : [];
        const remainingTime = typeof questionsPayload?.remainingTime === 'number'
          ? questionsPayload.remainingTime
          : null;
        const answersMap = questionsPayload?.answers || {};
        const sessionToken = questionsPayload?.sessionToken || null;
        const currentQuestionIndex = questionsPayload?.currentQuestionIndex || 0;
        const violations = questionsPayload?.violations || 0;
        console.log('qRes =', qRes);
        console.log('qRes.data =', qRes.data);
        console.log('questionsData =', questionsData);
        console.log('questionsData.length =', questionsData.length);
        setCodeDrafts(questionsPayload?.codeDrafts || {});
        setCodeSubmissions(questionsPayload?.codeSubmissions || {});
        if (questionsPayload?.codeMaxSubmissions) setCodeMaxSubmissions(questionsPayload.codeMaxSubmissions);
        initSession({
          token,
          sessionToken,
          driveInfo: driveInfoData,
          questions: questionsData,
          remainingTime,
          answers: answersMap,
          currentQuestionIndex: questionsPayload?.currentQuestionIndex,
          violationCount: questionsPayload?.violationCount
        });
      } catch (err) {
        if (!isMounted) return;
        const msg = err.response?.data?.message || err.message || 'Invalid Assessment Link';
        // Already submitted (or time over): show the submitted page instead of an error
        if (err.response?.status === 403 && /already submitted/i.test(msg)) {
          navigate(`/assess/${token}/submitted`, { replace: true });
          return;
        }
        setPublishedError(msg);
        console.error('Failed to fetch questions', err);
      } finally {
        if (isMounted) {
          setFetchingQs(false);
        }
      }
    };

    prepare();

    return () => {
      isMounted = false;
    };
  }, [token, initSession, navigate]);

  const handleFinalSubmit = useCallback(async () => {
    if (isSubmitted) return;
    await submitAssessment();
    navigate(`/assess/${token}/submitted`, { replace: true });
  }, [isSubmitted, navigate, submitAssessment, token]);

  // Timer logic
  useEffect(() => {
    if (status !== 'active' || isSubmitted) return;

    if (timeRemaining <= 0) {
      handleFinalSubmit();
      return;
    }

    const timer = setInterval(() => {
      tickTimer();
    }, 1000);

    return () => clearInterval(timer);
  }, [status, timeRemaining, isSubmitted, tickTimer, handleFinalSubmit]);

  // Violation logic
  useEffect(() => {
    if (status !== 'active' || isSubmitted) return;

    const maxViolations = driveInfo?.maxViolations || 3;
    // Deduplicate violation events that may be triggered by multiple browser events
    const VIOLATION_DEBOUNCE_MS = 1200;

    const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || 
      (typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0) && window.innerWidth < 1024);

    const handleViolationEvent = (type, message) => {
      const now = Date.now();
      if (now - lastViolationAtRef.current < VIOLATION_DEBOUNCE_MS) {
        // Ignore duplicate events fired within debounce window
        return;
      }
      lastViolationAtRef.current = now;

      addViolation(type);
      if (violations + 1 >= maxViolations) {
        showCustomAlert(
          `WARNING: ${message}!\nViolation ${violations + 1} of ${maxViolations}.\n\nMaximum violations reached. Your assessment will be auto-submitted.`,
          () => handleFinalSubmit()
        );
      } else {
        showCustomAlert(`WARNING: ${message}!\nViolation ${violations + 1} of ${maxViolations}.`);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) handleViolationEvent('TAB_SWITCH', 'You switched tabs or minimized the browser');
    };
    
    const handleWindowBlur = () => {
      // On mobile devices, window.blur fires on virtual keyboard popup/dismiss,
      // native dropdown selects, status bar touches, and screen orientation changes.
      // Only penalize true window blurs on desktop devices.
      if (!isMobileDevice) {
        handleViolationEvent('WINDOW_BLUR', 'You clicked outside the assessment window');
      }
    };

    const handleFullscreenChange = () => {
      // Only fire fullscreen exit if fullscreen API was actively engaged and supported
      if (!document.fullscreenElement && !isMobileDevice && document.fullscreenEnabled) {
        handleViolationEvent('FULLSCREEN_EXIT', 'You exited fullscreen mode');
      }
    };

    const handleCopy = (e) => {
      e.preventDefault();
      handleViolationEvent('COPY_ATTEMPT', 'Copying text is not allowed');
    };

    const handlePaste = (e) => {
      e.preventDefault();
      handleViolationEvent('PASTE_ATTEMPT', 'Pasting text is not allowed');
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      handleViolationEvent('RIGHT_CLICK', 'Right-click is disabled');
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("copy", handleCopy);
    document.addEventListener("paste", handlePaste);
    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [status, isSubmitted, violations, addViolation, driveInfo, handleFinalSubmit, showCustomAlert]);

  const handleOptionSelect = async (optionId) => {
    const qId = questions[currentQuestionIndex].id;
    setSaving(true);
    await setAnswer(qId, optionId);
    setSaving(false);
  };

  const handleClearAnswer = async () => {
    const qId = questions[currentQuestionIndex].id;
    setSaving(true);
    await setAnswer(qId, null);
    setSaving(false);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (publishedError) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center p-3">
        <div className="text-center">
          <h3 className="text-danger fw-bold">{publishedError === 'Assessment is not published yet' ? 'Assessment is not published yet' : 'Invalid Assessment Link'}</h3>
          <p className="text-muted">{publishedError}</p>
        </div>
      </div>
    );
  }

  if (fetchingQs && questions.length === 0) {
    return <div className="vh-100 d-flex justify-content-center align-items-center"><div className="spinner-border text-primary-custom"></div></div>;
  }

  if (questions.length === 0) {
    return <div className="vh-100 d-flex justify-content-center align-items-center p-3"><p>No questions available.</p></div>;
  }

  const safeQuestionIndex = Math.min(Math.max(0, currentQuestionIndex), Math.max(0, questions.length - 1));
  const currentQuestion = questions[safeQuestionIndex];
  const maxViolations = driveInfo?.maxViolations || 3;
  // A coding question counts as answered once the student has submitted code for it
  const isQuestionAnswered = (q) => (q.type === 'CODING'
    ? Boolean(codeSubmissions[q.id])
    : answers[q.id] !== undefined && answers[q.id] !== null);
  const answeredCount = questions.filter(isQuestionAnswered).length;
  const isCodingQuestion = currentQuestion.type === 'CODING';

  return (
    <div className="d-flex flex-column bg-light" style={{ height: '100dvh', overflow: 'hidden' }}>
      {/* Top Navbar */}
      <div className="bg-white border-bottom px-2 px-md-4 py-2 d-flex justify-content-between align-items-center shadow-sm z-1 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <h5 className="m-0 fw-bold text-primary-custom text-truncate" style={{ maxWidth: '180px' }} title={driveInfo?.companyName || 'Assessment'}>
            {driveInfo?.companyName || 'Assessment'}
          </h5>
          <span className="badge bg-light text-dark border d-none d-sm-inline-flex align-items-center">
            {saving ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Saving...</> : <><FaCloudUploadAlt className="me-1 text-success"/>Auto-saved</>}
          </span>
          {/* Mobile Question Palette Toggle */}
          <button 
            className="btn btn-sm btn-outline-primary d-md-none d-flex align-items-center gap-1 rounded-pill"
            onClick={() => setShowMobilePalette(true)}
            title="Open Question Palette"
            aria-label="Question Palette"
          >
            <FaTh /> <span className="small fw-semibold">{answeredCount}/{questions.length}</span>
          </button>
        </div>
        <div className="d-flex align-items-center gap-2 gap-md-3">
          {violations > 0 && (
            <div className="text-danger fw-bold d-flex align-items-center gap-1 bg-danger bg-opacity-10 px-2 px-md-3 py-1 rounded-pill border border-danger small">
              <FaLaptop /> {violations}/{maxViolations}
            </div>
          )}
          <div className={`fs-5 fs-md-4 fw-bold px-2 px-md-3 py-1 rounded-pill ${timeRemaining < 300 ? 'bg-danger text-white' : 'bg-light border'}`} style={{ fontFamily: 'monospace' }}>
            {formatTime(timeRemaining)}
          </div>
          <button className="btn btn-sm btn-md-md btn-primary-custom fw-bold px-3 px-md-4 rounded-pill" onClick={handleFinalSubmit}>
            Submit Test
          </button>
        </div>
      </div>

      <div className="d-flex flex-grow-1 overflow-hidden position-relative">
        {/* Left Panel: Question Area */}
        <div className="flex-grow-1 d-flex flex-column bg-white m-2 m-md-3 rounded-4 shadow-sm border overflow-hidden">
          <div className="bg-light p-2 p-md-3 border-bottom d-flex justify-content-between align-items-center">
            <h5 className="m-0 fw-bold fs-6 fs-md-5">Question {safeQuestionIndex + 1} of {questions.length}</h5>
            <div className="d-flex gap-1 gap-md-2">
              <span className="badge bg-success bg-opacity-10 text-success border border-success">+{currentQuestion.marks ?? 4} Marks</span>
              {Number(currentQuestion.negativeMarks) > 0 && (
                <span className="badge bg-danger bg-opacity-10 text-danger border border-danger">-{currentQuestion.negativeMarks} Marks</span>
              )}
            </div>
          </div>
          
          <div className="p-3 p-md-4 flex-grow-1 overflow-auto">
            {currentQuestion.difficulty && (
              <div className="mb-2 mb-md-3">
                <span className="badge bg-light text-dark border px-2 py-1 shadow-sm small">
                  {currentQuestion.difficulty.toLowerCase() === 'easy' ? '🟢 Easy' :
                   currentQuestion.difficulty.toLowerCase() === 'medium' ? '🟡 Medium' :
                   '🔴 Hard'}
                </span>
              </div>
            )}
            {isCodingQuestion ? (
              <CodingPanel
                key={currentQuestion.id}
                token={token}
                question={currentQuestion}
                initialDraft={codeDrafts[currentQuestion.id]}
                initialSubmission={codeSubmissions[currentQuestion.id]}
                maxSubmissions={codeMaxSubmissions}
                onDraftChange={(qId, draft) => setCodeDrafts(prev => ({ ...prev, [qId]: draft }))}
                onSubmitted={(qId, summary) => setCodeSubmissions(prev => ({ ...prev, [qId]: summary }))}
              />
            ) : (
            <>
            <h5 className="lh-base mb-3 mb-md-4 text-dark fs-6 fs-md-5" style={{ whiteSpace: 'pre-line' }}>{currentQuestion.text}</h5>
            
            <div className="d-flex flex-column gap-2 gap-md-3">
              {currentQuestion.options?.map((opt) => {
                const isSelected = answers[currentQuestion.id] === opt.id;
                return (
                  <div 
                    key={opt.id} 
                    className={`p-3 border rounded-3 cursor-pointer transition-all option-touch-item ${isSelected ? 'border-primary-custom bg-primary bg-opacity-10 shadow-sm' : 'border-light-subtle bg-white hover-bg-light'}`}
                    onClick={() => handleOptionSelect(opt.id)}
                  >
                    <div className="form-check d-flex align-items-center gap-2 m-0 p-0">
                      <input 
                        className="form-check-input m-0 flex-shrink-0" 
                        type="radio" 
                        checked={isSelected}
                        onChange={() => {}}
                        style={{ width: '1.25em', height: '1.25em' }}
                      />
                      <label className="form-check-label w-100 ms-2 text-dark fs-6 cursor-pointer mb-0" style={{ cursor: 'pointer' }}>
                        {opt.text}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
            )}
          </div>

          <div className="p-2 p-md-3 border-top bg-light d-flex flex-wrap gap-2 justify-content-between align-items-center">
            {isCodingQuestion ? <span /> : (
            <button 
              className="btn btn-sm btn-md-md btn-outline-secondary d-flex align-items-center gap-1 gap-md-2 fw-semibold"
              onClick={handleClearAnswer}
              disabled={answers[currentQuestion.id] === undefined || answers[currentQuestion.id] === null}
            >
              <FaTimesCircle /> Clear
            </button>
            )}
            
            <div className="d-flex gap-2">
              {safeQuestionIndex > 0 && (
                <button 
                  className="btn btn-sm btn-md-md btn-outline-primary d-flex align-items-center gap-1 gap-md-2 fw-semibold"
                  onClick={() => setCurrentQuestion(safeQuestionIndex - 1)}
                >
                  <FaChevronLeft /> Previous
                </button>
              )}
              {safeQuestionIndex < questions.length - 1 ? (
                <button 
                  className="btn btn-sm btn-md-md btn-primary-custom d-flex align-items-center gap-1 gap-md-2 fw-semibold px-3 px-md-4"
                  onClick={() => setCurrentQuestion(safeQuestionIndex + 1)}
                >
                  Save & Next <FaChevronRight />
                </button>
              ) : (
                <button 
                  className="btn btn-sm btn-md-md btn-success d-flex align-items-center gap-1 gap-md-2 fw-semibold px-3 px-md-4"
                  onClick={handleFinalSubmit}
                >
                  Submit Assessment
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel: Palette (Desktop) */}
        <div className="bg-white m-3 ms-0 rounded-4 shadow-sm border d-none d-md-flex flex-column" style={{ width: '300px', minWidth: '280px' }}>
          <div className="p-3 border-bottom bg-light rounded-top-4">
            <h6 className="m-0 fw-bold text-center">Question Palette</h6>
          </div>
          <div className="p-3 flex-grow-1 overflow-auto">
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              {questions.map((q, idx) => {
                const isAnswered = isQuestionAnswered(q);
                const isCurrent = safeQuestionIndex === idx;
                
                let btnClass = 'btn-outline-secondary'; 
                if (isAnswered) btnClass = 'btn-success text-white border-success';
                
                return (
                  <button 
                    key={q.id}
                    className={`btn fw-bold p-0 d-flex align-items-center justify-content-center ${btnClass} ${isCurrent ? 'ring-2 ring-primary' : ''}`}
                    style={{ width: '42px', height: '42px', borderRadius: '10px', opacity: isCurrent ? 1 : 0.85, borderWidth: isCurrent ? '3px' : '1px' }}
                    onClick={() => setCurrentQuestion(idx)}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-3 border-top bg-light rounded-bottom-4">
            <div className="d-flex justify-content-between small fw-semibold text-muted mb-2">
              <span className="d-flex align-items-center gap-2"><div className="bg-success rounded-circle" style={{width:'12px', height:'12px'}}></div> Answered</span>
              <span>{answeredCount}</span>
            </div>
            <div className="d-flex justify-content-between small fw-semibold text-muted">
              <span className="d-flex align-items-center gap-2"><div className="border border-secondary rounded-circle" style={{width:'12px', height:'12px'}}></div> Not Answered</span>
              <span>{questions.length - answeredCount}</span>
            </div>
          </div>
        </div>

        {/* Mobile Question Palette Drawer */}
        {showMobilePalette && (
          <div 
            className="d-md-none position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex justify-content-end"
            style={{ zIndex: 1050 }}
            onClick={() => setShowMobilePalette(false)}
          >
            <div 
              className="bg-white h-100 p-3 shadow-lg d-flex flex-column mobile-palette-drawer" 
              style={{ width: '85%', maxWidth: '340px' }} 
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                <h6 className="m-0 fw-bold">Question Palette</h6>
                <button className="btn btn-sm btn-light rounded-circle p-1" onClick={() => setShowMobilePalette(false)}>
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="p-3 flex-grow-1 overflow-auto">
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  {questions.map((q, idx) => {
                    const isAnswered = isQuestionAnswered(q);
                    const isCurrent = safeQuestionIndex === idx;
                    
                    let btnClass = 'btn-outline-secondary'; 
                    if (isAnswered) btnClass = 'btn-success text-white border-success';
                    
                    return (
                      <button 
                        key={q.id}
                        className={`btn fw-bold p-0 d-flex align-items-center justify-content-center ${btnClass} ${isCurrent ? 'ring-2 ring-primary' : ''}`}
                        style={{ width: '45px', height: '45px', borderRadius: '12px', opacity: isCurrent ? 1 : 0.85, borderWidth: isCurrent ? '3px' : '1px' }}
                        onClick={() => {
                          setCurrentQuestion(idx);
                          setShowMobilePalette(false);
                        }}
                      >
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="p-3 border-top bg-light rounded-3">
                <div className="d-flex justify-content-between small fw-semibold text-muted mb-2">
                  <span className="d-flex align-items-center gap-2"><div className="bg-success rounded-circle" style={{width:'12px', height:'12px'}}></div> Answered</span>
                  <span>{answeredCount}</span>
                </div>
                <div className="d-flex justify-content-between small fw-semibold text-muted">
                  <span className="d-flex align-items-center gap-2"><div className="border border-secondary rounded-circle" style={{width:'12px', height:'12px'}}></div> Not Answered</span>
                  <span>{questions.length - answeredCount}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <CustomAlertModal 
        show={alertConfig.show} 
        message={alertConfig.message} 
        onHide={hideCustomAlert} 
      />
    </div>
  );
};

export default AssessmentPage;
